/**
 * DEVELOPER DOCUMENTATION
 *
 * Include your custom JavaScript here.
 *
 * The theme Focal has been developed to be easily extensible through the usage of a lot of different JavaScript
 * events, as well as the usage of custom elements (https://developers.google.com/web/fundamentals/web-components/customelements)
 * to easily extend the theme and re-use the theme infrastructure for your own code.
 *
 * The technical documentation is summarized here.
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A VARIANT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a the user has changed the variant in a selector. The target get you the form
 * that triggered this event.
 *
 * Example:
 *
 * document.addEventListener('variant:changed', function(event) {
 *   let variant = event.detail.variant; // Gives you access to the whole variant details
 *   let form = event.target;
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * MANUALLY CHANGE A VARIANT
 * ------------------------------------------------------------------------------------------------------------
 *
 * You may want to manually change the variant, and let the theme automatically adjust all the selectors. To do
 * that, you can get the DOM element of type "<product-variants>", and call the selectVariant method on it with
 * the variant ID.
 *
 * Example:
 *
 * const productVariantElement = document.querySelector('product-variants');
 * productVariantElement.selectVariant(12345);
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN A NEW VARIANT IS ADDED TO THE CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever a variant is added to the cart through a form selector (product page, quick
 * view...). This event DOES NOT include any change done through the cart on an existing variant. For that,
 * please refer to the "cart:updated" event.
 *
 * Example:
 *
 * document.addEventListener('variant:added', function(event) {
 *   var variant = event.detail.variant; // Get the variant that was added
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * BEING NOTIFIED WHEN THE CART CONTENT HAS CHANGED
 * ------------------------------------------------------------------------------------------------------------
 *
 * This event is fired whenever the cart content has changed (if the quantity of a variant has changed, if a variant
 * has been removed, if the note has changed...). This event will also be emitted when a new variant has been
 * added (so you will receive both "variant:added" and "cart:updated"). Contrary to the variant:added event,
 * this event will give you the complete details of the cart.
 *
 * Example:
 *
 * document.addEventListener('cart:updated', function(event) {
 *   var cart = event.detail.cart; // Get the updated content of the cart
 * });
 *
 * ------------------------------------------------------------------------------------------------------------
 * REFRESH THE CART/MINI-CART
 * ------------------------------------------------------------------------------------------------------------
 *
 * If you are adding variants to the cart and would like to instruct the theme to re-render the cart, you cart
 * send the cart:refresh event, as shown below:
 *
 * document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', {
 *   bubbles: true
 * }));
 *
 * ------------------------------------------------------------------------------------------------------------
 * USAGE OF CUSTOM ELEMENTS
 * ------------------------------------------------------------------------------------------------------------
 *
 * Our theme makes extensive use of HTML custom elements. Custom elements are an awesome way to extend HTML
 * by creating new elements that carry their own JavaScript for adding new behavior. The theme uses a large
 * number of custom elements, but the two most useful are drawer and popover. Each of those components add
 * a "open" attribute that you can toggle on and off. For instance, let's say you would like to open the cart
 * drawer, whose id is "mini-cart", you simply need to retrieve it and set its "open" attribute to true (or
 * false to close it):
 *
 * document.getElementById('mini-cart').open = true;
 *
 * Thanks to the power of custom elements, the theme will take care automagically of trapping focus, maintaining
 * proper accessibility attributes...
 *
 * If you would like to create your own drawer, you can re-use the <drawer-content> content. Here is a simple
 * example:
 *
 * // Make sure you add "aria-controls", "aria-expanded" and "is" HTML attributes to your button:
 * <button type="button" is="toggle-button" aria-controls="id-of-drawer" aria-expanded="false">Open drawer</button>
 *
 * <drawer-content id="id-of-drawer">
 *   Your content
 * </drawer-content>
 *
 * The nice thing with custom elements is that you do not actually need to instantiate JavaScript yourself: this
 * is done automatically as soon as the element is inserted to the DOM.
 *
 * ------------------------------------------------------------------------------------------------------------
 * THEME DEPENDENCIES
 * ------------------------------------------------------------------------------------------------------------
 *
 * While the theme tries to keep outside dependencies as small as possible, the theme still uses third-party code
 * to power some of its features. Here is the list of all dependencies:
 *
 * "vendor.js":
 *
 * The vendor.js contains required dependencies. This file is loaded in parallel of the theme file.
 *
 * - custom-elements polyfill (used for built-in elements on Safari - v1.0.0): https://github.com/ungap/custom-elements
 * - web-animations-polyfill (used for polyfilling WebAnimations on Safari 12, this polyfill will be removed in 1 year - v2.3.2): https://github.com/web-animations/web-animations-js
 * - instant-page (v5.1.0): https://github.com/instantpage/instant.page
 * - tocca (v2.0.9); https://github.com/GianlucaGuarini/Tocca.js/
 * - seamless-scroll-polyfill (v2.0.0): https://github.com/magic-akari/seamless-scroll-polyfill
 *
 * "flickity.js": v2.2.0 (with the "fade" package). Flickity is only loaded on demand if there is a product image
 * carousel on the page. Otherwise it is not loaded.
 *
 * "photoswipe": v4.1.3. PhotoSwipe is only loaded on demand to power the zoom feature on product page. If the zoom
 * feature is disabled, then this script is never loaded.
 */

function observeElement(el, cbIsOnViewport, cbIsOutViewport) {
  const observer = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      const intersecting = entry.isIntersecting;
      if (intersecting) cbIsOnViewport();
      else cbIsOutViewport();
    });
  });
  observer.observe(el);
  return () => observer.unobserve(el);
}

window.observeElement = observeElement;

window.waitForElm = (selector) => {
  return new Promise((resolve) => {
    if (document.querySelector(selector)) {
      return resolve(document.querySelector(selector));
    }

    const observer = new MutationObserver((mutations) => {
      if (document.querySelector(selector)) {
        resolve(document.querySelector(selector));
        observer.disconnect();
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  });
};

window.toggleChangeHeartIconOnHeader = () => {
  const newIcon =
    document.querySelectorAll(".swym-added").length || location.href === "/pages/wishlist"
      ? `
      <svg class="icon icon--header-heart-filled   " width="25" height="23" viewBox="0 0 25 23" fill="none" xmlns="http://www.w3.org/2000/svg">
        <path d="M19.5002 0.999968C16.3002 0.599968 13.5002 3.16664 12.5002 4.49997C11.6669 3.33331 9.1002 0.999994 5.5002 0.999968C1.0002 0.999936 -0.9998 7 2.5002 12C5.3002 16 10.3335 20.3333 12.5002 22C14.1669 20.5 18.4002 16.5 22.0002 12.5C26.5002 7.5 23.5002 1.49997 19.5002 0.999968Z" fill="#C6B0BC"></path>
        <path d="M12.5 23C12.3274 22.9995 12.1597 22.9406 12.0234 22.8327C7.67891 19.3902 4.68624 16.4259 2.59216 13.5014C-0.0801489 9.76416 -0.689622 6.31376 0.779364 3.24585C1.82641 1.05448 4.8347 -0.738452 8.35089 0.305435C10.0274 0.79928 11.4901 1.85834 12.5 3.3096C13.5099 1.85834 14.9726 0.79928 16.6491 0.305435C20.1575 -0.722514 23.1736 1.05448 24.2206 3.24585C25.6896 6.31376 25.0801 9.76416 22.4078 13.5014C20.3138 16.4259 17.3211 19.3902 12.9766 22.8327C12.8402 22.9406 12.6726 22.9995 12.5 23V23ZM6.35057 1.60432C5.51392 1.57111 4.68419 1.7709 3.95046 2.18224C3.21672 2.59358 2.60669 3.20094 2.18584 3.93912C0.974708 6.47313 1.52167 9.29401 3.85798 12.5532C6.34086 15.8192 9.24824 18.7252 12.5 21.1911C15.7512 18.7276 18.6586 15.8243 21.142 12.5611C23.4861 9.29401 24.0253 6.47313 22.8142 3.94708C22.0328 2.35336 19.6887 1.08636 17.0789 1.83541C16.242 2.08761 15.4663 2.5157 14.8015 3.09211C14.1367 3.66853 13.5976 4.3805 13.2189 5.18222C13.16 5.32837 13.0599 5.45337 12.9312 5.54135C12.8025 5.62932 12.651 5.6763 12.4961 5.6763C12.3412 5.6763 12.1897 5.62932 12.061 5.54135C11.9323 5.45337 11.8322 5.32837 11.7733 5.18222C11.3975 4.3785 10.8593 3.66486 10.1941 3.08808C9.52886 2.51131 8.75163 2.08442 7.91332 1.83541C7.40541 1.68496 6.87947 1.60719 6.35057 1.60432V1.60432Z" fill="#1A1919" style="fill: #C6B0BC;"></path>
      </svg>
    `
      : `<svg class="icon icon--header-heart   " width="25" height="23" viewBox="0 0 25 23" fill="none" xmlns="http://www.w3.org/2000/svg">
      <path d="M12.5 23C12.3274 22.9995 12.1597 22.9406 12.0234 22.8327C7.67891 19.3902 4.68624 16.4259 2.59216 13.5014C-0.0801489 9.76416 -0.689622 6.31376 0.779364 3.24585C1.82641 1.05448 4.8347 -0.738451 8.35089 0.305435C10.0274 0.79928 11.4901 1.85834 12.5 3.3096C13.5099 1.85834 14.9726 0.79928 16.6491 0.305435C20.1575 -0.722514 23.1736 1.05448 24.2206 3.24585C25.6896 6.31376 25.0801 9.76416 22.4078 13.5014C20.3138 16.4259 17.3211 19.3902 12.9766 22.8327C12.8403 22.9406 12.6726 22.9995 12.5 23ZM6.35057 1.60432C5.51392 1.57111 4.68419 1.7709 3.95046 2.18224C3.21672 2.59358 2.60669 3.20094 2.18584 3.93912C0.974708 6.47313 1.52167 9.29401 3.85798 12.5532C6.34086 15.8192 9.24824 18.7252 12.5 21.1911C15.7512 18.7276 18.6586 15.8243 21.142 12.5611C23.4861 9.29401 24.0253 6.47313 22.8142 3.94708C22.0328 2.35336 19.6887 1.08636 17.0789 1.83541C16.242 2.08761 15.4663 2.5157 14.8015 3.09211C14.1367 3.66853 13.5976 4.3805 13.2189 5.18222C13.16 5.32837 13.0599 5.45337 12.9312 5.54135C12.8025 5.62932 12.651 5.6763 12.4961 5.6763C12.3412 5.6763 12.1897 5.62932 12.061 5.54135C11.9323 5.45337 11.8322 5.32837 11.7733 5.18222C11.3975 4.3785 10.8593 3.66486 10.1941 3.08808C9.52886 2.51131 8.75163 2.08442 7.91332 1.83541C7.40541 1.68496 6.87947 1.60719 6.35057 1.60432Z" fill="#1A1919"></path>
    </svg>`;

  document.querySelector(".header__icon-list a.swym-wishlist").innerHTML = newIcon;
};

class CustomVideo extends HTMLElement {
  constructor() {
    super();
    this.render();
    window.addEventListener("resize", () => this.render());
  }

  calculateHeightVideo() {
    const isDesktop = window.innerWidth >= 750;
    const thumbnail = this.querySelector(`.video__thumbnail${isDesktop ? ".desktop" : ".mobile"}`);
    const width = (thumbnail && thumbnail.clientWidth) || "100%";
    const height = (thumbnail && thumbnail.clientHeight) || 600;
    return { width, height };
  }

  renderYoutubeVideo(isAutoPlay) {
    const videoWrapper = this.querySelector(".video__player.youtube");
    const { width, height } = this.calculateHeightVideo();
    const videoId = videoWrapper.dataset?.videoId;

    if (videoId) {
      const onPlayerReady = (event) => event.target.playVideo();
      const options = {
        height,
        width,
        videoId,
        playerVars: {
          playsinline: 1,
          enablejsapi: 1,
          origin: 1,
        },
      };

      if (isAutoPlay) {
        options.events = { onReady: onPlayerReady };
      }

      new YT.Player("video-wrapper", options);
    }
  }

  renderHTML5Video() {
    const videoId = this.getAttribute("id");
    const videoWrapper = this.querySelector(".video__player.html5-video");

    const temp = videoWrapper.getElementsByTagName("template")[0];
    const clon = temp.content.cloneNode(true);
    videoWrapper.replaceChild(clon, temp);

    const video = document.getElementById(`html5-video--${videoId}`);

    const playVideo = () => video.play();
    const pauseVideo = () => video.pause();
    observeElement(video, playVideo, pauseVideo);
  }

  triggerPlayVideo(elm) {
    if (elm) {
      const isDesktop = window.innerWidth >= 750;
      const thumbnail = this.querySelector(`.video__thumbnail${isDesktop ? ".desktop" : ".mobile"}`);
      elm.addEventListener("click", () => {
        if (this.dataset.source === "youtube") {
          this.renderYoutubeVideo();
          thumbnail.style.display = "none";
        } else {
          this.renderHTML5Video();
        }
      });
    }
  }

  autoPlayVideo() {
    if (this.dataset.source === "youtube") {
      setTimeout(() => this.renderYoutubeVideo(true), 300);
    } else {
      setTimeout(() => this.renderHTML5Video(), 300);
    }
  }

  render() {
    if (this) {
      const autoplay = this.getAttribute("autoplay");

      if (autoplay !== undefined) {
        this.autoPlayVideo();
      }

      this.querySelectorAll(".button__wrapper").forEach((btn) => {
        this.triggerPlayVideo(btn);
      });
    }
  }
}

customElements.define("custom-video", CustomVideo);

class CommonTabs extends HTMLElement {
  constructor() {
    super();
    this.render();
    window.addEventListener("resize", () => this.render());
  }

  setChecked(elm) {
    elm.setAttribute("checked", "");
  }

  removeChecked(elm) {
    elm.removeAttribute("checked");
  }

  toggleCurrentLabelForCheckedInput(index) {
    this.querySelectorAll("label").forEach((lbl, lblIndex) => {
      if (lblIndex === index) {
        lbl.classList.add("active");
        lbl.scrollIntoView({ behavior: "smooth", block: "nearest" });
      } else {
        lbl.classList.remove("active");
      }
    });
  }

  toggleChecked(elm, index, nodesList) {
    elm.addEventListener("change", () => {
      this.toggleCurrentLabelForCheckedInput(index);
      this.setChecked(elm);
      const restInputs = nodesList ? [...nodesList].filter((_, idx) => idx !== index) : [];
      restInputs.forEach((rI) => this.removeChecked(rI));
    });
  }

  render() {
    const radios = this.querySelectorAll('input[type="radio"]');
    radios.forEach((elm, index, nodesList) => {
      this.toggleChecked(elm, index, nodesList);
    });
  }
}

customElements.define("common-tabs", CommonTabs);

class DropdownList extends HTMLElement {
  constructor() {
    super();
    this.render();
  }

  closeDropdownList() {
    const dropdownList = this.querySelector(".dropdown__list");
    if (dropdownList.classList.contains("show")) {
      dropdownList.classList.remove("show");
      this.setAriaLabel(false);
    }
  }

  toggleDropdownList() {
    const ariaLabel = this.querySelector(".dropdown__trigger").getAttribute("aria-expanded");
    this.querySelector(".dropdown__list").classList.toggle("show");
    this.setAriaLabel(ariaLabel !== "true");
  }

  setAriaLabel(isClicked = false) {
    this.querySelector(".dropdown__trigger").setAttribute("aria-expanded", isClicked ? "true" : "false");
  }

  render() {
    document.addEventListener("click", (e) => {
      const trigger = this.querySelector(".dropdown__trigger");
      if (e.target.matches(".dropdown__trigger")) {
        this.toggleDropdownList();
      }
      if (!trigger.contains(e.target)) {
        this.closeDropdownList();
      }
    });
  }
}

customElements.define("dropdown-list", DropdownList);
